import { TabItem } from 'component/Tabs/Tabs.types'
import capitalize from 'lodash/capitalize'
import { useCallback, useEffect, useRef, useState } from 'react'
import { TabsRefProps } from '../../components/MapContentTabs/MapContentTabs'
import countriesStateAcronymToStateName from '../../utils/countriesStateAcronymToStateName'

type UseFilterableMapMethodsProps = {
  country: string
  initialSelectedState: Record<string, string>
  setSelectedTab: (tab: TabItem) => void
  updateQuery: (country: string) => void
}

const useFilterableMapMethods = ({
  country,
  initialSelectedState,
  setSelectedTab,
  updateQuery
}: UseFilterableMapMethodsProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<TabsRefProps>({ getTabs: () => [] })
  const filteredObjectsRef = useRef<HTMLDivElement>(null)

  const storedStatesRef = useRef<{
    [x: string]: string
  }>(initialSelectedState)

  const [selectedState, setSelectedState] = useState<string>(
    initialSelectedState[country] ?? ''
  )

  const onStateChange = (state: string, shouldScrollToObjectsList = true) => {
    const scrollToObjectsListing = () => {
      filteredObjectsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }

    if (state === selectedState) {
      scrollToObjectsListing()

      return
    }

    setSelectedState(state)

    storedStatesRef.current[country] = state

    const fetchState = countriesStateAcronymToStateName[country][state]

    updateQuery(fetchState)

    if (shouldScrollToObjectsList && !!selectedState) {
      scrollToObjectsListing()
    }
  }

  const onTabChange = (tab: TabItem) => {
    setSelectedTab(tab)

    const newMode = tab.uuid

    if (newMode === 'federal') {
      setSelectedState('all')

      updateQuery(capitalize(country))
    } else {
      const previousState = storedStatesRef.current[country] ?? ''

      setSelectedState(previousState)

      onStateChange(previousState, false)

      mapContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const onCountryChange = useCallback(
    (updatedCountry: string) => {
      const { getTabs } = tabsRef.current

      const firstTab = getTabs()[0]

      if (firstTab?.uuid === 'federal') {
        setSelectedState('all')
      } else if (firstTab?.uuid === 'state') {
        setSelectedState(storedStatesRef.current[updatedCountry] ?? '')
      }

      setSelectedTab(firstTab)
      updateQuery(capitalize(updatedCountry))
    },
    [setSelectedTab, updateQuery]
  )

  useEffect(() => {
    if (country) {
      onCountryChange(country)
    }
  }, [country, onCountryChange])

  return {
    onTabChange,
    onStateChange,
    selectedState,
    mapContainerRef,
    filteredObjectsRef,
    tabsRef
  }
}

export default useFilterableMapMethods
